import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import CombinedTextField from '../../components/CombinedTextField';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import Select from '../../components/Select';
import { MAX_AMOUNT_LIMIT, MIN_AMOUNT_LIMIT } from '../../constants/numberLimit';
import analytics from '../../lib/analytics';
import applyDefaultCurrencyOn from '../../lib/defaultCurrencyHelper';
import useDefaultCurrency from '../../lib/hooks/useDefaultCurrency';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';

const EstimatedAssetsPage = ({ dispatchAddPageData, dispatchWizardNav, assets }) => {
  const [defaultCurrency, currencies] = useDefaultCurrency();

  // Savings
  const { currency: initialSavingsCurrency, amount: initialSavingsAmount } = applyDefaultCurrencyOn(
    assets.savings || {},
    defaultCurrency,
  );

  const [savingsAmount, setSavingsAmount] = useState(initialSavingsAmount);
  const [savingsCurrency, setSavingsCurrency] = useState(initialSavingsCurrency);

  // Investments
  const {
    currency: initialInvestmentsCurrency,
    amount: initialInvestmentsAmount,
  } = applyDefaultCurrencyOn(assets.investments || {}, defaultCurrency);

  const [investmentsAmount, setInvestmentsAmount] = useState(initialInvestmentsAmount);
  const [investmentsCurrency, setInvestmentsCurrency] = useState(initialInvestmentsCurrency);

  // Property
  const {
    currency: initialPropertyCurrency,
    amount: initialPropertyAmount,
  } = applyDefaultCurrencyOn(assets.property || {}, defaultCurrency);

  const [propertyAmount, setPropertyAmount] = useState(initialPropertyAmount);
  const [propertyCurrency, setPropertyCurrency] = useState(initialPropertyCurrency);

  const handleApplyDefault = event => {
    event.preventDefault();

    analytics.sendEvent("Click - Don't own assets", 'estimated-assets');

    const noAssetsPayload = payloadFactory.createAddPageDataPayload('estimatedAssets', {
      savings: { currency: 'USD', amount: '0' },
      investments: { currency: 'USD', amount: '0' },
      property: { currency: 'USD', amount: '0' },
    });

    dispatchAddPageData(noAssetsPayload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  const renderNoAssetsLink = () => {
    const totalAssets = [savingsAmount, investmentsAmount, propertyAmount].reduce(
      (total, amount) => {
        const parsedAmount = Number.parseFloat(amount) || 0;
        return total + parsedAmount;
      },
      0,
    );

    if (isNaN(totalAssets) || totalAssets === 0) {
      return (
        <div className="row-spacer">
          <Link to="/has-debt" onClick={handleApplyDefault}>
            Don&apos;t own any assets.
          </Link>
        </div>
      );
    }
    return null;
  };

  const handleSubmit = event => {
    event.preventDefault();

    const payload = payloadFactory.createAddPageDataPayload('estimatedAssets', {
      savings: { currency: savingsCurrency, amount: savingsAmount },
      investments: { currency: investmentsCurrency, amount: investmentsAmount },
      property: { currency: propertyCurrency, amount: propertyAmount },
    });
    dispatchAddPageData(payload);

    analytics.sendEvent('Click - Next', 'estimated-assets');

    dispatchWizardNav({ dir: 'fwd' });
  };

  const updateCurrency = (event, setter) => {
    event.preventDefault();

    setter(event.target.value);
  };

  const updateAmount = (event, setter) => {
    event.preventDefault();

    const value = event.target.value;
    if (Number.isNaN(Number(value))) {
      return;
    }

    const regex = /^[0-9]*$/;
    if (!regex.test(value)) {
      return;
    }

    if (value.length <= 10) {
      setter(value);
    }
  };

  return (
    <PageLayout
      title="Do you have any assets in your name?"
      helpText="Remember, our loans don't require collateral. We only need the approximate amount of your assets to better understand your financial status."
    >
      <form onSubmit={handleSubmit}>
        <div className="row-spacer">
          <p className="text-field__input-header">Savings</p>
          <CombinedTextField>
            <Select
              id="personal-savings-currency"
              data-testid="personal-savings-currency"
              value={savingsCurrency}
              onChange={e => updateCurrency(e, setSavingsCurrency)}
              options={currencies}
            />

            <InputField
              type="text"
              inputMode="numeric"
              pattern="\d*"
              id="personal-savings-amount"
              data-testid="personal-savings-amount"
              placeholder="Enter amount"
              value={savingsAmount}
              onChange={e => updateAmount(e, setSavingsAmount)}
              min={MIN_AMOUNT_LIMIT}
              max={MAX_AMOUNT_LIMIT}
            />
          </CombinedTextField>
        </div>

        <div className="row-spacer">
          <p className="text-field__input-header">Investment</p>
          <CombinedTextField>
            <Select
              id="investment-currency"
              data-testid="investment-currency"
              value={investmentsCurrency}
              onChange={e => updateCurrency(e, setInvestmentsCurrency)}
              options={currencies}
            />

            <InputField
              type="text"
              inputMode="numeric"
              pattern="\d*"
              id="investment-amount"
              data-testid="investment-amount"
              placeholder="Enter amount"
              value={investmentsAmount}
              onChange={e => updateAmount(e, setInvestmentsAmount)}
              min={0}
              max={9999999999}
            />
          </CombinedTextField>
        </div>

        <div className="row-spacer">
          <p className="text-field__input-header">Property</p>
          <CombinedTextField>
            <Select
              id="property-currency"
              data-testid="property-currency"
              value={propertyCurrency}
              onChange={e => updateCurrency(e, setPropertyCurrency)}
              options={currencies}
            />

            <InputField
              type="text"
              inputMode="numeric"
              pattern="\d*"
              id="property-amount"
              data-testid="property-amount"
              placeholder="Enter amount"
              value={propertyAmount}
              onChange={e => updateAmount(e, setPropertyAmount)}
              min={0}
              max={9999999999}
            />
          </CombinedTextField>
        </div>

        {renderNoAssetsLink()}

        <div className="row-spacer">
          <Button type="submit">Next</Button>
        </div>
      </form>
    </PageLayout>
  );
};

EstimatedAssetsPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  assets: PropTypes.shape({
    savings: PropTypes.shape({
      currency: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
    }).isRequired,
    investments: PropTypes.shape({
      currency: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
    }).isRequired,
    property: PropTypes.shape({
      currency: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export const mapStateToProps = reduxStore => ({
  assets: reduxStore.builder.ui.estimatedAssets,
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { EstimatedAssetsPage };

export default connect(mapStateToProps, mapDispatchToProps)(EstimatedAssetsPage);
